import { CircularProgress, Theme, useTheme } from "@material-ui/core";
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslate } from "react-admin";
import { handleAxiosError } from "../../../../../common/data/axiosErrorHandler";
import {
  MaintenanceDashboardApiFactory,
  TotalVehiclesWidgetDto,
} from "../../../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../../../common/hooks/useAxiosInstance";
import { SelectedVehiclesFilterContext, WidgetProps } from "../../common";
import { TotalVehiclesCircularIndicators } from "./TotalVehiclesCircularIndicators";
import { Widget } from "../Widget";
import { TotalVehiclesLegend } from "./TotalVehiclesLegend";

export const TotalVehicles = forwardRef(
  ({ className, icon, title, ...rest }: WidgetProps, ref) => {
    const [total, setTotal] = useState<TotalVehiclesWidgetDto>();
    const [loading, setLoading] = useState<boolean>(true);
    const axios = useAxiosInstance();
    const translate = useTranslate();
    const theme = useTheme<Theme>();

    const filterContext = useContext(SelectedVehiclesFilterContext);
    useEffect(() => {
      if (!axios) {
        return;
      }

      setLoading(true);
      MaintenanceDashboardApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppMaintenancedashboardTotalactivevehiclesGet()
        .then((result) => {
          setTotal(result.data);
        })
        .catch((e) => console.error(handleAxiosError(e)))
        .finally(() => setLoading(false));
    }, [axios]);

    const handleDiagnosticErrorsClick = useCallback(() => {
      if (!filterContext || !total?.vehiclesWithDiagnosticErrors) {
        return;
      }

      filterContext.setSelectedFilter("WITH_DIAGNOSTIC_ERRORS");
    }, [filterContext, total]);

    const handleGeometrisEventsClick = useCallback(() => {
      if (!filterContext || !total?.vehiclesWithCriticalGeometrisEvents) {
        return;
      }

      filterContext.setSelectedFilter("WITH_ELD_ALERTS");
    }, [filterContext, total]);

    const handleTpmsAlertsClick = useCallback(() => {
      if (!filterContext || !total?.vehiclesWithTpmsAlerts) {
        return;
      }

      filterContext.setSelectedFilter("WITH_TPMS_ALERTS");
    }, [filterContext, total]);

    const handleUnresolvedDvirDefectsClick = useCallback(() => {
      if (!filterContext || !total?.vehiclesWithUnresolvedDvirDefects) {
        return;
      }

      filterContext.setSelectedFilter("WITH_UNRESOLVED_DVIR_DEFECTS");
    }, [filterContext, total]);

    return (
      <Widget
        title={title}
        icon={icon}
        className={className}
        ref={ref}
        {...rest}
      >
        <div className="total-vehicles categories">
          {loading ? (
            <div className="category">
              <CircularProgress size="3rem" />
            </div>
          ) : (
            <>
              <TotalVehiclesCircularIndicators total={total} />
              <div className="total-vehicles-categories">
                <TotalVehiclesLegend
                  label={translate(
                    "resources.maintenance-dashboard.labels.withDiagnosticErrors"
                  )}
                  value={total?.vehiclesWithDiagnosticErrors}
                  borderColor={theme.palette.error.main}
                  onClick={handleDiagnosticErrorsClick}
                />
                <TotalVehiclesLegend
                  label={translate(
                    "resources.maintenance-dashboard.labels.withEldAlerts"
                  )}
                  value={total?.vehiclesWithCriticalGeometrisEvents}
                  borderColor={theme.palette.warning.main}
                  onClick={handleGeometrisEventsClick}
                />
                <TotalVehiclesLegend
                  label={translate(
                    "resources.maintenance-dashboard.labels.withTpmsAlerts"
                  )}
                  value={total?.vehiclesWithTpmsAlerts}
                  borderColor={theme.palette.error.main}
                  onClick={handleTpmsAlertsClick}
                />
                <TotalVehiclesLegend
                  label={translate(
                    "resources.maintenance-dashboard.labels.withUnresolvedDvirDefects"
                  )}
                  value={total?.vehiclesWithUnresolvedDvirDefects}
                  borderColor={theme.palette.warning.main}
                  onClick={handleUnresolvedDvirDefectsClick}
                />
              </div>
            </>
          )}
        </div>
      </Widget>
    );
  }
);
